import React, { useState, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { VehicleDetails } from '../../types';
import { vehicleContext } from '../VehicleProvider';
import useStaticVehicleData from '../../hooks/useStaticVehicleData';
import links from '../../lib/links';
import { moneyFormat, moneyParse, formatPrice } from '../../utils/currencyTools';
import Modal from '../Modal';
import {
  Container,
  InnerContainer,
  Price,
  TotalKey,
  TotalValue,
  LegalCtaContainer,
  LegalCta,
  CtaContainer,
  DealerListPrice,
  DealerName,
  TotalValueContainer,
  TotalKeyContainer,
  HeaderWrapper,
  SecondaryRow,
  PrimaryRow,
  PrimaryCta,
  SecondaryCta,
} from './PriceBreakdown.styles';
import { COLORS } from '../../styles/variables';
import LeviesAndFeesCollapse from '../LeviesAndFeesCollapse';
import {
  Legal,
  DisclaimerTitleWrapper,
  TextContainer,
} from '../PriceBreakdownInfoBox-T3/PriceBreakdown-T3.styles';
import { HeadlineOrderTwo } from '../Common/Common.style';

interface PricedItems {
  formatted: string;
  label: string;
  price: number;
}

const PriceBreakdownInfoBox: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const lang = i18n.languages?.[0] || 'en';

  const { vehicle, market } = useContext(vehicleContext);
  const {
    modelPrice,
    dealerId,
    typedPrices,
    audiCode,
    modelID,
    vehicleType,
    warrantyType,
    modelMake,
    stockNumber,
    modelName,
    modelYear,
    isAvailableNow,
    dealerName,
  } = (vehicle as VehicleDetails) ?? {};

  const isJdpVehicle = useMemo(() => {
    if (vehicle?.dataSource === 'AGC_USA_JDP' || vehicle?.dataSource === 'AGC_CAN_JDP') return true;
    return false;
  }, [vehicle?.dataSource]);

  const nonAudiModelName = useMemo(() => {
    if (modelName && modelName.split(' ').length > 3)
      return modelName.split(' ').slice(1).slice(-2).join(' ');

    if (modelName) return modelName.split(' ').pop();

    return undefined;
  }, [modelName]);

  // account for new carline ID format
  const [modelId] = modelID?.split('-') ?? [];

  const staticVehicle = useStaticVehicleData(modelId);

  // Todo nonAudi to be determined
  const nonAudi = new URLSearchParams(window.location.search).get('type') === 'nonaudi';
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  if (!vehicle) return null;

  const rapidName = staticVehicle?.rapid_name ?? '';

  const getTotalFeesNumber = (priceArray: PricedItems[] | undefined) =>
    priceArray?.reduce((total, fee) => {
      return total + moneyParse(fee.formatted, lang);
    }, 0);

  const fees = typedPrices?.reduce((acc: PricedItems[], fee) => {
    const price = { formatted: fee.formatted, label: '', price: 0 };
    switch (fee.type) {
      // Air Conditioning Levy
      case 'CUSTOM_TAXATION':
        price.label = t(`pricingBreakdown.priceDetails.item2`);
        price.price = moneyParse(price.formatted, lang);
        acc[1] = price;
        break;
      // Tire Levy
      case 'CUSTOM_TAXATION_2':
        price.label = t(`pricingBreakdown.priceDetails.item3`);
        price.price = moneyParse(price.formatted, lang);
        acc[2] = price;
        break;
      // Luxury Tax
      // Remove the luxury tax line item in the short term from the pricing breakdown.
      // case 'CUSTOM_TAXATION_3':
      // price.label = t(`pricingBreakdown.priceDetails.luxury`);
      // price.price = moneyParse(price.formatted);
      // acc[4] = price;
      // break;
      // Freight and PDI
      case 'CUSTOM_PRICE_1':
        price.label = t(`pricingBreakdown.priceDetails.item1`);
        price.price = moneyParse(price.formatted, lang);
        acc[0] = price;
        break;
      // Motor Vehicle Council Fee
      case 'CUSTOM_PRICE_3':
        price.label = t(`pricingBreakdown.priceDetails.item4`);
        price.price = moneyParse(price.formatted, lang);
        acc[3] = price;
        break;
      default:
    }
    return acc;
  }, []);

  const jdpFees = typedPrices?.reduce((acc: PricedItems[], fee) => {
    const price = { formatted: formatPrice(fee?.amount), label: '', price: 0 };
    switch (fee.type) {
      // Air Conditioning Levy
      case 'acLevy':
        price.label = t(`pricingBreakdown.priceDetails.item2`);
        price.price = fee?.amount || 0;
        acc[1] = price;
        break;
      // Tire Levy
      case 'tireLevy':
        price.label = t(`pricingBreakdown.priceDetails.item3`);
        price.price = fee?.amount || 0;
        acc[2] = price;
        break;
      // Luxury Tax
      // Remove the luxury tax line item in the short term from the pricing breakdown.
      // case 'luxuryTax':
      // price.label = t(`pricingBreakdown.priceDetails.luxury`);
      // price.price = moneyParse(fee?.amount);
      // acc[4] = price;
      // break;
      // Freight and PDI
      case 'freightPDI':
        price.label = t(`pricingBreakdown.priceDetails.item1`);
        price.price = fee?.amount || 0;
        acc[0] = price;
        break;
      // Motor Vehicle Council Fee
      case 'motoCouncil':
        price.label = t(`pricingBreakdown.priceDetails.item4`);
        price.price = fee?.amount || 0;
        acc[3] = price;
        break;
      default:
    }
    return acc;
  }, []);

  if (vehicle.modelID === 'rsq8' && vehicleType === 'new') {
    const greenLevyPrice: PricedItems = {
      formatted: '$1,000.00',
      label: t(`pricingBreakdown.priceDetails.greenLevy`),
      price: 1000,
    };
    if (isJdpVehicle) jdpFees.push(greenLevyPrice);
    else {
      fees.push(greenLevyPrice);
    }
  }
  if (
    vehicle.modelID === 'r8spyderperformance' &&
    vehicleType === 'new' &&
    vehicle?.driveType === 'quattro'
  ) {
    const greenLevyPrice: PricedItems = {
      formatted: '$2,000.00',
      label: t(`pricingBreakdown.priceDetails.greenLevy`),
      price: 2000,
    };
    if (isJdpVehicle) jdpFees.push(greenLevyPrice);
    else {
      fees.push(greenLevyPrice);
    }
  }

  const totalFeesNumber = getTotalFeesNumber(isJdpVehicle ? jdpFees : fees);
  const basePriceNumber = modelPrice && moneyParse(modelPrice, lang);

  // const vehicleCodeCategory =
  //   market === 'ca'
  //     ? {
  //         type: 'ca',
  //         slug: 'nc',
  //         carcode: 'newcarcode',
  //       }
  //     : {
  //         type: 'cauc',
  //         slug: 'uc',
  //         carcode: 'preownedcarcode',
  //       };

  const formsLink = links[lang]?.formsUrl ?? links.en.formsUrl;

  const tradeInUrl =
    lang &&
    rapidName &&
    dealerId &&
    `${formsLink}?modelid=${rapidName}&dealer_id=49${dealerId}`.replace(/\s/g, '%20');

  const nonAudiTradeInUrl = `${formsLink}?modelMake=${modelMake}&modelName=${
    nonAudiModelName ?? ''
  }&modelYear=${modelYear}&dealer_id=49${dealerId}`.replace(/\s/g, '%20');

  // const paymentEstimatorUrl =
  //   dealerId &&
  //   audiCode &&
  //   `${links.paymentEstimator?.url}${lang}/dealer/49${dealerId}/${vehicleCodeCategory.carcode}/${audiCode}`;
  // const contactDealer = links.contactDealer.url;
  // const contactDealerUrl = `https://www.audi.ca/ca/web/${lang}/forms${contactDealer}?modelid=${rapidName}&dealer_id=49${dealerId}&stock_number=${stockNumber}&audi_code=${audiCode}&source=vtp-${
  //   market === 'ca' && !isAvailableNow
  //     ? `${vehicleCodeCategory?.slug}-in-transit`
  //     : vehicleCodeCategory?.slug
  // }`;

  // const paymentEstimatorOrContactDealer = () => {
  //   if (vehicleType === 'used' && !warrantyType) {
  //     return (
  //       <PrimaryCta href={contactDealerUrl} data-testid="contact-dealer-button">
  //         {t('pricingBreakdown.priceDetails.contactDealer')}
  //       </PrimaryCta>
  //     );
  //   }

  //   return (
  //     <PrimaryCta href={paymentEstimatorUrl} data-testid="payment-estimator-button">
  //       {t('pricingBreakdown.priceDetails.ctaPrimary')}
  //     </PrimaryCta>
  //   );
  // };

  return (
    <>
      <Container data-testid="info-box-container">
        <InnerContainer>
          <PrimaryRow>
            <DealerListPrice>
              <HeaderWrapper>
                <TotalKeyContainer>
                  <TotalKey>{t(`pricingBreakdown.priceDetails.subHeading3`)}</TotalKey>
                  {!isMobile && (
                    <DealerName>
                      {t(`pricingBreakdown.priceDetails.fromDealer`)} {dealerName}
                    </DealerName>
                  )}
                </TotalKeyContainer>
                <TotalValueContainer>
                  <TotalValue data-testid="dealer-list-price">
                    {typeof basePriceNumber === 'number' && moneyFormat(basePriceNumber, lang)}
                    <LegalCtaContainer
                      onClick={onModalOpen}
                      data-testid="modal-open-button"
                      aria-label={t('pricingBreakdown.ariaLabel.information')}
                    >
                      <LegalCta>1</LegalCta>
                    </LegalCtaContainer>
                  </TotalValue>
                </TotalValueContainer>
              </HeaderWrapper>
              {isMobile && (
                <DealerName>
                  {t(`pricingBreakdown.priceDetails.fromDealer`)} {dealerName}
                </DealerName>
              )}
            </DealerListPrice>
          </PrimaryRow>
          <SecondaryRow>
            <p>{t(`pricingBreakdown.priceDetails.subHeading1`)}</p>
            <Price data-testid="suggested-retail-price">
              {typeof totalFeesNumber === 'number' &&
                typeof basePriceNumber === 'number' &&
                moneyFormat(basePriceNumber - totalFeesNumber, lang)}
            </Price>
          </SecondaryRow>
          <LeviesAndFeesCollapse
            fees={isJdpVehicle ? jdpFees : fees}
            totalFeesNumber={totalFeesNumber}
          />
        </InnerContainer>
        <CtaContainer>
          <SecondaryCta
            href={nonAudi ? nonAudiTradeInUrl : tradeInUrl}
            data-testid="request-trade-button"
          >
            {t('pricingBreakdown.priceDetails.ctaSecondary')}
          </SecondaryCta>
          {/* {!nonAudi && paymentEstimatorOrContactDealer()} */}
        </CtaContainer>
      </Container>
      <Modal isOpen={modalOpen} onClose={onModalClose} data-cy="legal-disclaimer-modal">
        <Legal>
          <DisclaimerTitleWrapper>
            <HeadlineOrderTwo color={COLORS.black}>
              {t(`pricingBreakdown.priceDetails.legalDisclaimer`)}
            </HeadlineOrderTwo>
          </DisclaimerTitleWrapper>
          <TextContainer>
            {nonAudi
              ? t('pricingBreakdown.priceDetails.nonAudiLegalCopy')
              : t('pricingBreakdown.priceDetails.legalCopy')}
          </TextContainer>
        </Legal>
      </Modal>
    </>
  );
};

export default PriceBreakdownInfoBox;
